<script setup lang="ts">
import { PhArrowClockwise as Refresh, PhChartBar as ChartBar, PhWarning as Warning } from "@phosphor-icons/vue";
import { computed, onMounted, onUnmounted, ref } from "vue";

interface MetricData {
  uv: number;
  pv: number;
  startedVisitors: number;
  completedVisitors: number;
  testsCompleted: number;
  repeatCompletedVisitors: number;
  repeatCompletionAttempts: number;
  completionRate: number;
  postersGenerated: number;
  identitiesSaved: number;
  linksCopied: number;
  stateVisitors: number;
  stateCompleted: number;
  plansGenerated: number;
  plansClaimed: number;
  contactQrLoaded: number;
  contactQrFailed: number;
  contactsAdded: number;
  averageTestSeconds: number;
  returnRate: number;
}

interface Summary {
  generatedAt: string;
  metrics: MetricData;
  funnel: Array<{ name: string; value: number; conversion: number }>;
  questionDropoff: Array<{ id: string; answered: number; dropped: number; dropoffRate: number }>;
  stateDistribution: Array<{ questionId: string; options: Array<{ value: string; label: string; count: number }> }>;
  channels: Array<{ name: string; uv: number }>;
  devices: Array<{ name: string; uv: number }>;
  browsers: Array<{ name: string; uv: number }>;
  errors: Array<{ name: string; count: number }>;
}

interface MetricCard {
  label: string;
  value: string | number;
  note: string;
  primary?: boolean;
}

interface MetricGroup {
  title: string;
  description: string;
  featured?: boolean;
  cards: MetricCard[];
}

const range = ref("all");
const summary = ref<Summary>();
const loading = ref(false);
const error = ref("");
let timer = 0;

const metricGroups = computed<MetricGroup[]>(() => {
  const data = summary.value?.metrics;
  if (!data) return [];
  return [
    {
      title: "访问",
      description: "区分访问人数与页面浏览次数",
      cards: [
        { label: "访问人数（UV）", value: data.uv, note: "按浏览器访客 ID 去重" },
        { label: "页面浏览量（PV）", value: data.pv, note: "全部页面访问次数" },
        { label: "回访率", value: `${data.returnRate}%`, note: "访问 2 次及以上人数 ÷ UV" },
      ],
    },
    {
      title: "测试完成",
      description: "“人数”按访客去重，“次数”按测试 ID 去重",
      featured: true,
      cards: [
        { label: "开始测试人数", value: data.startedVisitors, note: "去重访客" },
        { label: "完成测试人数", value: data.completedVisitors, note: "同一人完成多次只计 1 人", primary: true },
        { label: "完成测试次数", value: data.testsCompleted, note: "不同测试 ID 的完成次数" },
        { label: "重复完成人数", value: data.repeatCompletedVisitors, note: "完成 2 次及以上的访客" },
        { label: "额外重复次数", value: data.repeatCompletionAttempts, note: "每人首次完成之外的次数" },
        { label: "测试完成率", value: `${data.completionRate}%`, note: "完成人数 ÷ 开始人数" },
        { label: "平均测试耗时", value: `${data.averageTestSeconds}s`, note: "按测试 ID 去重后的均值" },
      ],
    },
    {
      title: "分享",
      description: "测试结果的后续传播动作",
      cards: [
        { label: "生成海报次数", value: data.postersGenerated, note: "进入海报页" },
        { label: "保存身份证次数", value: data.identitiesSaved, note: "成功保存到相册" },
        { label: "复制链接次数", value: data.linksCopied, note: "复制成功" },
      ],
    },
    {
      title: "状态与方案",
      description: "从状态确认到方案领取",
      cards: [
        { label: "进入状态确认人数", value: data.stateVisitors, note: "去重访客" },
        { label: "状态确认完成次数", value: data.stateCompleted, note: "完成两道方案定制题" },
        { label: "回血方案生成次数", value: data.plansGenerated, note: "生成工位方案" },
        { label: "领取方案次数", value: data.plansClaimed, note: "进入领取承接页" },
        { label: "二维码加载成功", value: data.contactQrLoaded, note: "接口成功返回二维码的次数" },
        { label: "二维码生成失败", value: data.contactQrFailed, note: "含首次失败与手动重试失败" },
        { label: "添加企微人数", value: data.contactsAdded, note: "首次有效回调，按用户 ID 去重" },
      ],
    },
  ];
});

const maxDimension = (rows: Array<{ uv: number }>) => Math.max(1, ...rows.map((row) => row.uv));
const maxState = (rows: Array<{ count: number }>) => Math.max(1, ...rows.map((row) => row.count));

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const response = await fetch(`/api/analytics?range=${range.value}`, { cache: "no-store" });
    if (!response.ok) throw new Error("统计服务暂不可用");
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) throw new Error("统计接口未启动，请启动本地统计服务后刷新");
    summary.value = await response.json() as Summary;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "统计服务暂不可用";
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  void load();
  timer = window.setInterval(load, 30000);
});
onUnmounted(() => window.clearInterval(timer));
</script>

<template>
  <section class="analytics-screen">
    <header class="analytics-header">
      <div>
        <span class="analytics-kicker"><ChartBar :size="18" weight="fill" /> NBTI DATA MONITOR</span>
        <h1>访问与转化统计</h1>
        <p>人数与次数分开统计 · 每 30 秒自动更新</p>
      </div>
      <div class="analytics-controls">
        <select v-model="range" aria-label="统计时间范围" @change="load">
          <option value="today">今天</option>
          <option value="7d">近7天</option>
          <option value="30d">近30天</option>
          <option value="all">全部</option>
        </select>
        <button type="button" :disabled="loading" @click="load"><Refresh :size="18" :class="{ spinning: loading }" /> 刷新</button>
      </div>
    </header>

    <div v-if="error" class="analytics-error"><Warning :size="20" weight="fill" /> {{ error }}</div>

    <template v-if="summary">
      <div class="metric-groups">
        <section v-for="group in metricGroups" :key="group.title" class="metric-group" :class="{ 'metric-group--featured': group.featured }">
          <header><h2>{{ group.title }}</h2><p>{{ group.description }}</p></header>
          <div class="metric-grid">
            <article v-for="card in group.cards" :key="card.label" class="metric-card" :class="{ 'metric-card--primary': card.primary }">
              <span>{{ card.label }}</span><strong>{{ card.value }}</strong><small>{{ card.note }}</small>
            </article>
          </div>
        </section>
      </div>

      <div class="analytics-layout">
        <section class="analytics-panel analytics-panel--wide">
          <div class="analytics-panel-title"><h2>全链路转化漏斗</h2><span>以首页UV为基准</span></div>
          <div class="funnel-list">
            <div v-for="step in summary.funnel" :key="step.name" class="funnel-row">
              <span>{{ step.name }}</span><div><i :style="{ width: `${Math.max(2, step.conversion)}%` }"></i></div><strong>{{ step.value }}</strong><small>{{ step.conversion }}%</small>
            </div>
          </div>
        </section>

        <section class="analytics-panel">
          <div class="analytics-panel-title"><h2>各题流失率</h2><span>按测试ID去重</span></div>
          <table><thead><tr><th>题目</th><th>作答</th><th>流失</th><th>流失率</th></tr></thead><tbody>
            <tr v-for="row in summary.questionDropoff" :key="row.id"><td>{{ row.id }}</td><td>{{ row.answered }}</td><td>{{ row.dropped }}</td><td>{{ row.dropoffRate }}%</td></tr>
          </tbody></table>
        </section>

        <section class="analytics-panel">
          <div class="analytics-panel-title"><h2>异常数据</h2><span>需重点关注</span></div>
          <div class="error-list"><div v-for="row in summary.errors" :key="row.name"><span>{{ row.name }}</span><strong>{{ row.count }}</strong></div></div>
        </section>

        <section class="analytics-panel analytics-panel--wide">
          <div class="analytics-panel-title"><h2>状态选项分布</h2><span>基于完成状态确认的数据</span></div>
          <div class="state-distributions">
            <div v-for="group in summary.stateDistribution" :key="group.questionId" class="state-distribution">
              <strong>{{ group.questionId }}</strong>
              <p v-if="!group.options.length">暂无数据</p>
              <div v-for="option in group.options" :key="option.value"><span>{{ option.label }}</span><i><b :style="{ width: `${option.count / maxState(group.options) * 100}%` }"></b></i><em>{{ option.count }}</em></div>
            </div>
          </div>
        </section>

        <section v-for="group in [{ title: '来源渠道', rows: summary.channels }, { title: '设备分布', rows: summary.devices }, { title: '浏览器分布', rows: summary.browsers }]" :key="group.title" class="analytics-panel">
          <div class="analytics-panel-title"><h2>{{ group.title }}</h2><span>UV</span></div>
          <div class="dimension-list">
            <p v-if="!group.rows.length">暂无数据</p>
            <div v-for="row in group.rows" :key="row.name"><span>{{ row.name }}</span><i><b :style="{ width: `${row.uv / maxDimension(group.rows) * 100}%` }"></b></i><strong>{{ row.uv }}</strong></div>
          </div>
        </section>
      </div>

      <footer class="analytics-updated">最后更新：{{ new Date(summary.generatedAt).toLocaleString('zh-CN') }}</footer>
    </template>
  </section>
</template>
